import { useMemo, useState } from 'react';
import { ClockIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useBackOrders, useOpenPurchaseOrders, useClosePurchaseOrder } from '../hooks/useReceiving';

// What has been ordered and not yet accepted.
//
// Outstanding is ordered minus ACCEPTED, so a line stays here until good stock
// actually arrives. Damaged and rejected quantities are shown beside it because
// they are the reason it is still outstanding — the difference between "the
// supplier has not sent it" and "they sent it and it was unusable", which is a
// different conversation with a different person.
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');
const money = (v) => '£' + Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


// One row per short line, flat rather than grouped: a spreadsheet is filtered
// and pivoted by whoever opens it, and a grouped export fights that. Exports
// what is on screen, so a search or supplier filter carries into the file.
const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
function exportBackOrdersCsv(rows) {
  const header = ['PO', 'Xero PO', 'Supplier', 'Storeroom', 'Requisition', 'Sent', 'Days waiting',
    'Item code', 'Item', 'Ordered', 'Received', 'Outstanding', 'Damaged', 'Rejected', 'Value outstanding'];
  const body = rows.map((r) => [
    r.po_number, r.xero_po_number || '', r.supplier_name, r.storeroom || '', r.requisition_ref || '',
    fmtDate(r.sent_at),
    r.sent_at ? Math.floor((Date.now() - new Date(r.sent_at).getTime()) / 86400000) : '',
    r.item_code, r.name,
    r.qty_ordered, r.qty_received, r.qty_outstanding, r.qty_damaged, r.qty_rejected,
    // Unformatted: a spreadsheet should get a number, not a pound sign.
    Number(r.value_outstanding || 0).toFixed(2),
  ]);
  const csv = [header, ...body].map((row) => row.map(csvCell).join(',')).join('\r\n');
  // The BOM is what makes Excel read UTF-8 rather than mangling it.
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = `back-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function Stat({ label, value, tone }) {
  const colour = tone === 'amber' ? 'text-amber-700' : tone === 'red' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className={`text-2xl font-bold tabular-nums ${colour}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function Age({ sentAt }) {
  if (!sentAt) return <span className="text-gray-400">—</span>;
  const days = Math.floor((Date.now() - new Date(sentAt).getTime()) / 86400000);
  const tone = days >= 28 ? 'bg-red-100 text-red-800'
    : days >= 14 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{days}d</span>;
}

// Settling an order is a separate act from receiving it. The storeman records
// what physically arrived; only the office knows whether the balance is still
// coming, has been credited, or is being written off — so only the office closes.
function CloseDialog({ po, reasons, onDone, onCancel }) {
  const [reason, setReason] = useState('complete');
  const [note, setNote] = useState('');
  const close = useClosePurchaseOrder();
  const owed = Number(po.qty_outstanding) || 0;
  const claims = (Number(po.qty_damaged) || 0) + (Number(po.qty_rejected) || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900">Close {po.po_number}</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          {po.supplier_name}
          {po.xero_po_number ? <span className="ml-2 font-mono text-xs text-gray-400">Xero {po.xero_po_number}</span> : null}
        </p>
        {owed > 0 && (
          <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {owed} unit(s) still outstanding
            {claims > 0 ? ` — ${po.qty_damaged} damaged, ${po.qty_rejected} rejected` : ''}.
            {' '}Closing accepts the balance is not coming on this order.
          </p>
        )}
        <label className="mt-4 block text-xs font-semibold text-gray-500">Why is it being closed?</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
          {Object.entries(reasons || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <label className="mt-3 block text-xs font-semibold text-gray-500">Note (credit note number, who agreed it)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        {close.isError && (
          <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {close.error?.response?.data?.error || 'Could not close that order.'}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
          <button type="button" disabled={close.isPending}
            onClick={() => close.mutate({ id: po.id, reason, note }, { onSuccess: onDone })}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {close.isPending ? 'Closing…' : 'Close order'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettlePanel() {
  const { data } = useOpenPurchaseOrders();
  const [closing, setClosing] = useState(null);
  const orders = data?.orders || [];
  if (!orders.length) return null;
  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">Purchase orders to settle</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Receiving no longer closes an order. Close it here once the balance is accounted for —
          delivered, credited, or written off.
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {orders.map((o) => {
          const owed = Number(o.qty_outstanding) || 0;
          return (
            <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="min-w-0 flex-1">
                {/* Both numbers: ours is what the requisition and GRN are filed
                    under, Xero's is what the supplier quotes back at you. */}
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-sm font-semibold text-gray-900">{o.po_number}</span>
                  {o.xero_po_number ? (
                    <span className="font-mono text-xs text-gray-500">Xero {o.xero_po_number}</span>
                  ) : null}
                  {o.storeroom ? (
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">{o.storeroom}</span>
                  ) : null}
                </span>
                <span className="block truncate text-sm text-gray-600">{o.supplier_name}</span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  {o.qty_received} of {o.qty_ordered} received
                  {owed > 0 ? ` · ${owed} outstanding` : ' · all counted'}
                  {' · sent '}{fmtDate(o.sent_at)}
                </span>
              </span>
              {Number(o.qty_damaged) > 0 && (
                <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{o.qty_damaged} damaged</span>
              )}
              {Number(o.qty_rejected) > 0 && (
                <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">{o.qty_rejected} rejected</span>
              )}
              <Age sentAt={o.sent_at} />
              <button type="button" onClick={() => setClosing(o)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:border-brand hover:text-brand">
                <CheckCircleIcon className="h-4 w-4" /> Close
              </button>
            </li>
          );
        })}
      </ul>
      {closing && (
        <CloseDialog po={closing} reasons={data?.reasons}
          onDone={() => setClosing(null)} onCancel={() => setClosing(null)} />
      )}
    </div>
  );
}

export default function BackOrders() {
  const { data, isLoading, isError } = useBackOrders();
  const [q, setQ] = useState('');
  // Memoised because the filter below depends on it: a fresh [] on every render
  // would re-run the filter on every keystroke for no reason.
  const rows = useMemo(() => data?.backOrders || [], [data]);
  const s = data?.summary || {};

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.name, r.item_code, r.po_number, r.xero_po_number, r.supplier_name, r.storeroom]
      .some((v) => String(v || '').toLowerCase().includes(needle)));
  }, [rows, q]);

  // Grouped by purchase order. A flat line list answers "what is short" but not
  // "which order is short and who is waiting for it" - the two questions the
  // office actually chases a supplier with. Ordered by the oldest PO first,
  // since age is what makes one worth a phone call.
  const byPo = useMemo(() => {
    const m = new Map();
    for (const r of shown) {
      if (!m.has(r.po_id)) {
        m.set(r.po_id, {
          po_id: r.po_id, po_number: r.po_number, xero_po_number: r.xero_po_number,
          supplier_name: r.supplier_name, sent_at: r.sent_at,
          storeroom: r.storeroom, requisition_ref: r.requisition_ref,
          lines: [], units: 0, value: 0, damaged: 0, rejected: 0,
        });
      }
      const g = m.get(r.po_id);
      g.lines.push(r);
      g.units += Number(r.qty_outstanding) || 0;
      g.value += Number(r.value_outstanding) || 0;
      g.damaged += Number(r.qty_damaged) || 0;
      g.rejected += Number(r.qty_rejected) || 0;
    }
    return [...m.values()];
  }, [shown]);

  const [openPo, setOpenPo] = useState(() => new Set());
  const togglePo = (id) => setOpenPo((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center gap-3">
        <ClockIcon className="h-9 w-9 text-brand" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Back Orders</h1>
          <p className="text-sm text-gray-500">
            Ordered and not yet accepted. A line clears only when good stock is received —
            damaged and rejected quantities stay outstanding until they are replaced.
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportBackOrdersCsv(shown)}
          disabled={!shown.length}
          className="ml-auto shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          <ArrowDownTrayIcon className="mr-1.5 inline h-4 w-4" />
          Export CSV
        </button>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Lines outstanding" value={s.lines ?? 0} />
        <Stat label="Units outstanding" value={s.units ?? 0} />
        <Stat label="Value outstanding" value={money(s.value)} />
        <Stat label="Damaged" value={s.damaged ?? 0} tone="amber" />
        <Stat label="Rejected" value={s.rejected ?? 0} tone="red" />
      </div>

      <SettlePanel />

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search item, code, PO or supplier"
          className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
        />
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {isError && <p className="text-sm text-red-600">Could not load back orders.</p>}

      {!isLoading && !isError && !rows.length && (
        <p className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          Nothing outstanding — every ordered line has been received in full.
        </p>
      )}

      {!isLoading && shown.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                {/* The PO, supplier and age live on the group header now, so
                    they are not repeated per line. Fixed widths on the numeric
                    columns so the figures line up down the page instead of
                    shifting with the length of each item name. */}
                <th className="px-3 py-2">Item</th>
                <th className="w-24 px-3 py-2 text-right">Ordered</th>
                <th className="w-24 px-3 py-2 text-right">Received</th>
                <th className="w-28 px-3 py-2 text-right">Outstanding</th>
                <th className="w-24 px-3 py-2 text-right">Damaged</th>
                <th className="w-24 px-3 py-2 text-right">Rejected</th>
                <th className="w-28 px-3 py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {byPo.map((g) => [
                // One header row per PO: the order, who it is with, where it is
                // owed, and what the whole order is still short.
                <tr key={`po-${g.po_id}`} className="bg-gray-50/80">
                  <td colSpan={7} className="px-3 py-2">
                    <button type="button" onClick={() => togglePo(g.po_id)}
                      className="flex w-full items-center gap-3 text-left">
                      {/* Both numbers: ours is what the requisition and the GRN
                          are filed under, Xero's is what the supplier quotes. */}
                      <span className="font-mono text-sm font-semibold text-gray-900">{g.po_number}</span>
                      {g.xero_po_number ? (
                        <span className="font-mono text-xs text-gray-500">Xero {g.xero_po_number}</span>
                      ) : null}
                      <span className="text-sm text-gray-600">{g.supplier_name}</span>
                      {g.storeroom ? (
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">
                          {g.storeroom}
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          no storeroom on the requisition
                        </span>
                      )}
                      {g.requisition_ref ? (
                        <span className="font-mono text-xs text-gray-400">{g.requisition_ref}</span>
                      ) : null}
                      <Age sentAt={g.sent_at} />
                      <span className="ml-auto text-xs text-gray-600">
                        <strong className="tabular-nums text-gray-900">{g.units}</strong> unit(s) short
                        {g.damaged ? <span className="ml-2 text-amber-700">{g.damaged} damaged</span> : null}
                        {g.rejected ? <span className="ml-2 text-red-700">{g.rejected} rejected</span> : null}
                        <span className="ml-3 tabular-nums text-gray-500">{money(g.value)}</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {openPo.has(g.po_id) ? 'hide' : `${g.lines.length} line(s)`}
                      </span>
                    </button>
                  </td>
                </tr>,
                ...(openPo.has(g.po_id) ? g.lines : []).map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 pl-6">
                    <div className="font-medium text-gray-800">{r.name}</div>
                    <div className="font-mono text-xs text-gray-500">{r.item_code}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.qty_ordered}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.qty_received}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{r.qty_outstanding}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${Number(r.qty_damaged) ? 'font-semibold text-amber-700' : 'text-gray-300'}`}>{r.qty_damaged}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${Number(r.qty_rejected) ? 'font-semibold text-red-700' : 'text-gray-300'}`}>{r.qty_rejected}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{money(r.value_outstanding)}</td>
                </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && rows.length > 0 && (s.damaged > 0 || s.rejected > 0) && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-900">
            {s.damaged > 0 && <><strong>{s.damaged}</strong> damaged</>}
            {s.damaged > 0 && s.rejected > 0 && ' and '}
            {s.rejected > 0 && <><strong>{s.rejected}</strong> rejected</>}
            {' '}unit{(s.damaged + s.rejected) === 1 ? '' : 's'} are counted in the outstanding figures above.
            They arrived but could not be accepted, so the supplier still owes them.
          </p>
        </div>
      )}
    </div>
  );
}
